// React
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
// MUI
import { Grid, Skeleton } from "@mui/material";
// Helpers
import { getOEMParameters, getSanitatedLocationOEMs } from "@/Common/helpers/OEMs";
// Hooks
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
// Graphql
import { gql } from "@/services/httpAdapter";
// Custom Styles
import "@/assets/styles/settings/FinancialStatementConfig.css";
// Components
import FinancialStatementConfigPage from "./FinancialStatementConfigPage";
import BodyLayer from "@/Layouts/components/Layers/BodyLayer";
import RoundedBox from "@/Layouts/components/Basics/RoundedBox";
import TabLoader from "@/Layouts/components/Skeletons/TabLoader";
import DefaultTiersLoader from "@/Layouts/components/Skeletons/DefaultTiersLoader/DefaultTiersLoader";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
// Icons
import cadillacLogo from "@/assets/icons/franchises/cadillac_icon.svg";
import chevroletLogo from "@/assets/icons/franchises/chevrolet_icon.svg";
import mitsubishiLogo from "@/assets/icons/franchises/mm.svg";
import volvoLogo from "@/assets/icons/franchises/volvo_text.svg";
import buickLogo from "@/assets/icons/franchises/buick_text.svg";
import gmcLogo from "@/assets/icons/franchises/gmc_icon.svg";
// Api
import { getFSVersionsByOEM, getAccountsByFSVersion } from "../../api/FinancialStatementConfig/queries";
import {
	updateFSVersionInLocation,
	updateFSAccountMappingConfig,
	saveGLAccountsFromPrevVersion,
} from "../../api/FinancialStatementConfig/mutations";
import { errorLogger } from "@/Common/helpers/logger";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";

const getPageParsed = (page) => {
	if (page?.content) {
		try {
			page.content = JSON.parse(page.content);
		} catch (e) {
			return page;
		}
	}

	return page;
};

const getIconToRender = (franchise) => {
	let franchiseIcon = null;
	if (franchise === "CHEVROLET") {
		franchiseIcon = chevroletLogo;
	} else if (franchise === "CADILLAC") {
		franchiseIcon = cadillacLogo;
	} else if (franchise === "MITSUBISHI") {
		franchiseIcon = mitsubishiLogo;
	} else if (franchise === "VOLVO CARS") {
		franchiseIcon = volvoLogo;
	} else if (franchise === "BUICK") {
		franchiseIcon = buickLogo;
	} else if (franchise === "GMC") {
		franchiseIcon = gmcLogo;
	}
	return franchiseIcon;
};

const RenderTabContent = ({
	oems,
	versions,
	pages,
	selectedVersion,
	selectedPage,
	handleSelectionPage,
	handleSelectionVersion,
	updateGLAccounts,
	copyGLAccounts,
	isLoading,
	localLoading,
	showCopyModal,
	accountTypes,
}) => {
	const formattedPages = pages.map((entry) => {
		return {
			...entry,
			pageNumberLabel: "Page " + entry.pageNumber,
			iconToRender: getIconToRender(entry.pageRelations?.franchise),
		};
	});
	return Object.entries(oems)?.map(([key], index) => (
		<Grid container key={`${location.locationId}_${key}_${index}`} style={{ marginLeft: "30px" }}>
			<Grid item xs={12} className={"models-tiered-area"}>
				<Grid container mt={"10px"} mb={"12px"} spacing={3}>
					<Grid item xs={2} sx={{ minWidth: "260px" }}>
						<SmartSelector
							selectBy={"version"}
							showBy={"versionLabel"}
							options={versions.map((entry) => ({ ...entry, versionLabel: "Version " + entry.version }))}
							selected={selectedVersion ? { ...selectedVersion, versionLabel: "Version " + selectedVersion?.version } : "-1"}
							inputProps={{
								placeholder: "Select Version",
							}}
							listBoxProps={{
								sx: { maxHeight: "220px" },
							}}
							loading={isLoading || !selectedVersion}
							onChangeAction={handleSelectionVersion}
						/>
					</Grid>
					<Grid item xs={2} sx={{ minWidth: "200px" }}>
						<SmartSelector
							selectBy={"pageNumber"}
							showBy={"pageNumberLabel"}
							options={formattedPages}
							selected={
								selectedPage
									? {
											...selectedPage,
											pageNumberLabel: "Page " + selectedPage?.pageNumber,
											iconToRender: getIconToRender(selectedPage?.pageRelations?.franchise),
									  }
									: "-1"
							}
							inputProps={{
								placeholder: "Select Page",
							}}
							listBoxProps={{
								sx: { maxHeight: "220px" },
							}}
							showIcon={"test"}
							loading={isLoading || !selectedPage}
							// error={Boolean(errors?.franchise)}
							onChangeAction={handleSelectionPage}
						/>
					</Grid>
				</Grid>
				{selectedPage && selectedVersion && !localLoading ? (
					<FinancialStatementConfigPage
						page={getPageParsed(selectedPage)}
						versions={versions.filter((version) => version.version !== selectedVersion.version)}
						updateGLAccounts={updateGLAccounts}
						copyGLAccounts={copyGLAccounts}
						isLoading={isLoading}
						showCopyModal={showCopyModal}
						accountTypes={accountTypes}
					/>
				) : (
					<Grid container mt={"10px"} mb={"12px"} spacing={3}>
						<Grid item xs={12}>
							<Skeleton width={500} height={34} />
						</Grid>
						<Grid item xs={3}>
							<Skeleton width={250} height={34} />
						</Grid>
						<Grid item xs={3}>
							<Skeleton width={250} height={34} />
						</Grid>
						<Grid item xs={3}>
							<Skeleton width={250} height={34} />
						</Grid>
					</Grid>
				)}
			</Grid>
		</Grid>
	));
};

const QUERY_IDENTIFIERS = ["fsConfigInfo", "fsAccountsPagesInfo"];

export default function FinancialStatementConfigLayer() {
	const { clearCache } = useCacheCleaner();
	const [oems, setOEMs] = useState([]);
	const [selectedOEM, setSelectedOEM] = useState(0);
	const [selectedVersion, setSelectedVersion] = useState(null);
	const [selectedPage, setSelectedPage] = useState(null);
	const [tabList, setTabList] = useState(null);
	const [localLoading, setLocalLoading] = useState(false);
	const location = useSelector((state) => state?.location?.location);

	const { getSubscription, addSubscription } = useSubscriptions();
	const queryClient = useQueryClient();
	// Subscriptions
	const fsAccountsPagesInfoSubscription = getSubscription(["saveGLAccountsFromPrevVersion", location?.locationId]);
	fsAccountsPagesInfoSubscription?.setInvalidate(["saveGLAccountsFromPrevVersion", location?.locationId]);
	fsAccountsPagesInfoSubscription?.subscriptions?.onSaveGLAccountsFromPrevVersion?.setCallback((result) => {
		if (location?.locationId && selectedVersion && selectedPage) {
			queryClient.setQueryData(
				[
					"fsAccountsPagesInfo",
					location?.locationId,
					fsConfigInfo?.[selectedOEM]?.oemReference,
					fsConfigInfo?.[selectedOEM]?.dealerCodeFS,
					fsConfigInfo?.[selectedOEM]?.versions?.version,
					selectedVersion?.version,
				],
				(cachedData) => {
					let newPage = {};
					cachedData.versions.financialStatements.pages.forEach((page) => {
						if (page.pageNumber === selectedPage.pageNumber) {
							newPage = result.data.onSaveGLAccountsFromPrevVersion.versions.financialStatements.pages.filter(
								(p) => p.pageNumber === selectedPage.pageNumber
							)[0];
							newPage = getPageParsed(newPage);
						}
					});
					const pageIdx = cachedData.versions.financialStatements.pages.findIndex(
						(page) => page.pageNumber === selectedPage.pageNumber
					);
					cachedData.versions.financialStatements.pages[pageIdx] = newPage;
					setSelectedPage(newPage);
					return cachedData;
				}
			);
		}
	});

	const fsAccountsMappingSubscription = getSubscription(["updateFSAccountMappingConfig", location?.locationId]);
	fsAccountsMappingSubscription?.setInvalidate(["updateFSAccountMappingConfig", location?.locationId]);
	fsAccountsMappingSubscription?.subscriptions?.onUpdateFSAccountMappingConfig?.setCallback((result) => {
		const resultData = result.data.onUpdateFSAccountMappingConfig;
		if (location?.locationId && selectedVersion && selectedPage) {
			queryClient.setQueryData(
				[
					"fsAccountsPagesInfo",
					location?.locationId,
					fsConfigInfo?.[selectedOEM]?.oemReference,
					fsConfigInfo?.[selectedOEM]?.dealerCodeFS,
					fsConfigInfo?.[selectedOEM]?.versions?.version,
					selectedVersion?.version,
				],
				(prevData) => {
					if (!prevData) return prevData;
					let pagesInfo = { ...prevData };
					// Verify the fs version is the same as current
					if (pagesInfo.versions.financialStatements.version === resultData.fsVersion) {
						const versions = pagesInfo.versions;
						const financialStatements = versions.financialStatements;
						let page = financialStatements.pages.find((page) => page.pageNumber === resultData.pageNumber);
						page = { ...getPageParsed(page) };
						page.content.map((row) => {
							if (row.account?.join() === resultData.account[0]?.account?.join()) {
								row.relations = row.relations.map((relation) => {
									if (relation.subAccount === resultData?.account[0]?.relations.subAccount) {
										relation.hideInput = resultData?.account[0]?.relations?.hideInput;
										relation.warning = resultData?.account[0]?.relations?.warning;
										relation.error = resultData?.account[0]?.relations?.error;
										relation.typeError = resultData?.account[0]?.relations?.typeError;
										relation.glAccounts[resultData.account[0]?.relations.GLIndex] = {
											glAccount: resultData?.account[0]?.relations.glAccountValue,
											accountType: resultData?.account[0]?.relations.accountType,
										};
									}
									return relation;
								});
							}
							return row;
						});
					}
					return pagesInfo;
				}
			);
		}
	});

	// Queries
	const { data: fsConfigInfo, isLoading } = useQuery({
		queryKey: ["fsConfigInfo", location?.locationId],
		queryFn: () => getFSVersionsByOEM(location?.locationId),
		staleTime: Infinity,
		enabled: !!location?.locationId,
	});

	const { data: fsAccountsPagesInfo, isLoadingPages } = useQuery({
		queryKey: [
			"fsAccountsPagesInfo",
			location?.locationId,
			fsConfigInfo?.[selectedOEM]?.oemReference,
			fsConfigInfo?.[selectedOEM]?.dealerCodeFS,
			fsConfigInfo?.[selectedOEM]?.versions?.version,
			selectedVersion?.version,
		],
		queryFn: () =>
			getAccountsByFSVersion(
				location?.locationId,
				fsConfigInfo?.[selectedOEM].oemReference,
				fsConfigInfo?.[selectedOEM].dealerCodeFS,
				fsConfigInfo?.[selectedOEM]?.versions?.version,
				selectedVersion?.version
			),
		staleTime: Infinity,
		enabled:
			!!location?.locationId &&
			!!fsConfigInfo?.[selectedOEM]?.oemReference &&
			!!fsConfigInfo?.[selectedOEM]?.dealerCodeFS &&
			!!fsConfigInfo?.[selectedOEM]?.versions?.version &&
			!!selectedVersion?.version,
	});

	// Mutations
	const { mutate: mutateUpdateFSVersionInLocation } = useMutation({
		mutationFn: ({ locationId, oemReference, dealerCodeFS, oemVersion, newFSVersion }) =>
			updateFSVersionInLocation(locationId, oemReference, dealerCodeFS, oemVersion, newFSVersion),
		onError: () => {
			errorLogger("Error updating version");
		},
	});

	const { mutate: mutateUpdateFSAccountMappingConfig, isPending: updatingFSAccount } = useMutation({
		mutationFn: ({ locationId, input }) => updateFSAccountMappingConfig(locationId, input),

		onError: () => {
			errorLogger("Error account map");
		},
	});

	const { mutate: mutateSaveGLAccountsFromPrevVersion, isPending: copyingFSAccounts } = useMutation({
		mutationFn: ({ locationId, input }) => saveGLAccountsFromPrevVersion(locationId, input),
		onError: () => {
			errorLogger("Error copying from previous version");
		},
	});

	const updateGLAccounts = (action, glValue, _id, account, label, relations) => {
		const updateInput = {
			oemReference: fsConfigInfo[selectedOEM]?.oemReference,
			dealerCodeFS: fsConfigInfo[selectedOEM]?.dealerCodeFS,
			oemVersion: fsConfigInfo[selectedOEM]?.versions?.version,
			fsVersion: selectedVersion.version,
			pageNumber: selectedPage.pageNumber,
			account: {
				account,
				label,
				relations,
			},
		};
		let foundIn = null;
		// validate that the GL account is not used
		fsAccountsPagesInfo?.versions?.financialStatements?.pages?.forEach((page) => {
			const pageContent =
				(typeof page.content === "string" ? JSON.parse(page.content) : Array.isArray(page.content) ? page.content : []) || [];
			pageContent.forEach((accountContent) => {
				//VALIDATE THAT IS NOT IN THE SUBACCOUNTS
				const subAccounts = accountContent?.relations?.glAccounts?.map((entry) => entry.glAccount);
				const mappedGlAccountList = accountContent?.glAccounts?.map((acc) => acc.glAccount) || [];
				// if GL Account is found, create the foundIn object with the details

				if (glValue !== "" && action !== "DELETE" && accountContent.account?.toString() !== account?.toString()) {
					if (mappedGlAccountList.includes(glValue) || subAccounts?.includes(glValue)) {
						foundIn = { pageNumber: page.pageNumber, account: accountContent.account };
						return foundIn;
					}
				}
			});
		});

		// if the gl account was not found, call the backend to write the account
		if (!foundIn) {
			mutateUpdateFSAccountMappingConfig({ locationId: location?.locationId, input: updateInput });
		}
		return foundIn;
	};

	const copyGLAccounts = (versionFrom) => {
		const copyGLAccountsInput = {
			oemReference: fsConfigInfo[selectedOEM].oemReference,
			dealerCodeFS: fsConfigInfo[selectedOEM].dealerCodeFS,
			oemVersion: fsConfigInfo[selectedOEM].versions.version,
			fsVersionFrom: versionFrom.version,
			fsVersionTo: selectedVersion.version,
			PageNumber: selectedPage.pageNumber,
		};
		const copyGLAccountsResponse = mutateSaveGLAccountsFromPrevVersion({
			locationId: location?.locationId,
			input: copyGLAccountsInput,
		});
		return copyGLAccountsResponse;
	};

	useEffect(() => {
		if (!location) return;

		// Check if fsConfigInfo is available and not loading
		if (fsConfigInfo && !isLoading) {
			const locationOEMs = getSanitatedLocationOEMs(location?.oems);
			const oemNames = fsConfigInfo.map((oem) => {
				const display = getOEMParameters({
					oemReference: oem?.oemReference,
					dealerCodeFS: oem?.dealerCodeFS,
					locationOEMs,
				});
				return display?.oemDisplayName || oem.versions?.oemName;
			});

			setOEMs(oemNames);
			const initialTabList = [];
			for (const oem of fsConfigInfo) {
				initialTabList.push({
					name: oem.versions?.oemName,
					selectedPage: null,
					selectedVersion: oem.versions.financialStatements[oem.versions.currentFSVersion],
				});
			}
			const selectedVersionIndex = fsConfigInfo[selectedOEM]?.versions?.currentFSVersion || 0;
			setSelectedOEM(fsConfigInfo?.[selectedOEM] ? selectedOEM : 0);
			setSelectedVersion(fsConfigInfo[selectedOEM]?.versions?.financialStatements?.[selectedVersionIndex]);
			setTabList(initialTabList);
			setLocalLoading(false);
		}
	}, [location, fsConfigInfo, isLoading, selectedOEM]);

	useEffect(() => {
		if (fsAccountsPagesInfo && !isLoadingPages && !isLoading) {
			if (tabList[selectedOEM]) {
				const newTabListWithPages = { ...tabList };
				newTabListWithPages[selectedOEM].selectedVersion = fsAccountsPagesInfo?.versions?.financialStatements;

				const tempSelectedPage = fsAccountsPagesInfo?.versions?.financialStatements?.pages?.find(
					(page) => page.pageNumber === selectedPage?.pageNumber
				);

				setSelectedPage(tempSelectedPage || fsAccountsPagesInfo?.versions?.financialStatements.pages[0]);
				setTabList(newTabListWithPages);
			}
			setLocalLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location?.locationId, fsAccountsPagesInfo, isLoadingPages]);

	useEffect(() => {
		return () => {
			clearCache(QUERY_IDENTIFIERS);
		};
	}, []);

	useEffect(() => {
		if (isLoading) return;
		if (tabList) {
			let newTabList = { ...tabList };
			const newSelectedPage =
				fsAccountsPagesInfo?.versions?.financialStatements.pages.find((page) => page.pageNumber === selectedPage?.pageNumber) ||
				fsAccountsPagesInfo?.versions?.financialStatements.pages[0] ||
				null;
			newTabList[selectedOEM] = {
				...newTabList[selectedOEM],
				selectedPage: newSelectedPage,
				selectedVersion: fsAccountsPagesInfo?.versions?.financialStatements,
			};
			setTabList(newTabList);

			addSubscription(
				["saveGLAccountsFromPrevVersion", location.locationId],
				[
					{
						context: "onSaveGLAccountsFromPrevVersion",
						query: gql`
							subscription onSaveGLAccountsFromPrevVersion($locationId: String!) {
								onSaveGLAccountsFromPrevVersion(locationId: $locationId) {
									locationId
									oemReference
									dealerCodeFS
									versions {
										_id
										financialStatements {
											version
											pages {
												pageName
												pageNumber
												pageRelations {
													franchise
												}
												content
												_id
											}
										}
									}
								}
							}
						`,
						variables: {
							locationId: location?.locationId,
						},
					},
				]
			);

			addSubscription(
				["updateFSAccountMappingConfig", location.locationId],
				[
					{
						context: "onUpdateFSAccountMappingConfig",
						query: gql`
							subscription onUpdateFSAccountMappingConfig($locationId: String!) {
								onUpdateFSAccountMappingConfig(locationId: $locationId) {
									locationId
									oemReference
									dealerCodeFS
									oemVersion
									fsVersion
									pageNumber
									account {
										_id
										account
										label
										relations {
											_id
											subAccount
											glAccountValue
											accountType
											GLIndex
											error
											typeError {
												glAccount
												expectedType
												currentType
											}
											warning
											hideInput
										}
									}
								}
							}
						`,
						variables: {
							locationId: location.locationId,
						},
					},
				]
			);
			setLocalLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location?.locationId, selectedVersion]);

	useEffect(() => {
		if (isLoading || isLoadingPages) {
			return;
		}
		if (tabList) {
			let newTabList = { ...tabList };
			const newSelectedPage = fsAccountsPagesInfo?.versions?.financialStatements.pages.find(
				(page) => page.pageNumber === selectedPage?.pageNumber
			);
			newTabList[selectedOEM] = { ...newTabList[selectedOEM], selectedPage: newSelectedPage, selectedVersion };
			setTabList(newTabList);
		}
		setLocalLoading(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedPage]);

	const handleSelectionVersion = (selected) => {
		if (isLoadingPages) {
			return;
		}
		//SAVE IN THE DATABASE THE SELECTED VERSION
		mutateUpdateFSVersionInLocation({
			locationId: location?.locationId,
			oemReference: fsConfigInfo?.[selectedOEM].oemReference,
			dealerCodeFS: fsConfigInfo?.[selectedOEM].dealerCodeFS,
			oemVersion: fsConfigInfo?.[selectedOEM]?.versions?.version,
			newFSVersion: selected.version,
		});
		try {
			setSelectedVersion(selected);
		} catch (e) {
			console.log("ERROR ", e);
		}
	};

	const handleSelectionPage = (selected) => {
		setLocalLoading(true);
		setSelectedPage(selected);
		// localLoading is set false in the useEffect of [selectedPage]
	};

	const handleTabChange = (idx) => {
		if (isLoadingPages) {
			return;
		}
		setSelectedPage(tabList?.[idx]?.selectedPage || null);
		setSelectedVersion(fsConfigInfo[idx].versions?.financialStatements[fsConfigInfo[idx].versions.currentFSVersion]);
		setSelectedOEM(idx);
	};
	const loadingSkeleton = () => {
		return (
			<Grid>
				<TabLoader ml={"35px"} switch />
				<Grid container ml={"35px"} mt={"35px"}>
					<Grid item xs={2}>
						<Skeleton width={238} height={35} />
					</Grid>
					<Grid item xs={2} ml={"35px"}>
						<Skeleton width={238} height={35} />
					</Grid>
					<Grid item xs={12} mt={"40px"}>
						<Skeleton width={700} height={35} />
					</Grid>
					<Grid item xs={2} mt={"30px"}>
						<Skeleton width={80} height={35} />
					</Grid>
					<Grid item xs={2} mt={"30px"}>
						<Skeleton width={80} height={35} />
					</Grid>
					<Grid item xs={2} mt={"30px"}>
						<Skeleton width={80} height={35} />
					</Grid>
				</Grid>
			</Grid>
		);
	};
	return (
		<BodyLayer>
			<RoundedBox sx={{ padding: "30px 0px !important", minHeight: "550px !important", background: "red" }}>
				{isLoading || !selectedVersion || isLoadingPages ? (
					loadingSkeleton()
				) : (
					<TabSelectable
						tabs={oems || []}
						style={{ marginLeft: "30px" }}
						content={RenderTabContent({
							oems,
							versions: fsConfigInfo?.[selectedOEM]?.versions?.financialStatements || [],
							pages: fsAccountsPagesInfo?.versions?.financialStatements.pages || [],
							selectedVersion: tabList?.[selectedOEM]?.selectedVersion,
							selectedPage: tabList?.[selectedOEM]?.selectedPage,
							handleSelectionPage,
							handleSelectionVersion,
							updateGLAccounts,
							copyGLAccounts,
							isLoading: isLoading || isLoadingPages || updatingFSAccount || copyingFSAccounts,
							localLoading,
							accountTypes: fsConfigInfo?.[selectedOEM]?.accountTypes || [],
						})}
						onTabChange={handleTabChange}
					/>
				)}
			</RoundedBox>
		</BodyLayer>
	);
}
